/**
 * Map viewport coordinates to a character offset inside a textarea.
 * Textareas do not expose reliable caretRangeFromPoint in Chromium; a styled
 * mirror div mirrors wrapping so we can binary-search the offset.
 */
export function textareaOffsetFromPoint(textarea: HTMLTextAreaElement, clientX: number, clientY: number): number {
  const value = textarea.value
  if (!value) return 0

  const style = window.getComputedStyle(textarea)
  const mirror = document.createElement('div')
  const rect = textarea.getBoundingClientRect()

  mirror.style.position = 'fixed'
  mirror.style.left = `${rect.left}px`
  mirror.style.top = `${rect.top}px`
  // Same border box and scrollbar gutter as the textarea, so lines wrap at the same glyph.
  mirror.style.width = `${rect.width}px`
  mirror.style.height = `${rect.height}px`
  mirror.style.overflowX = 'hidden'
  mirror.style.overflowY = textarea.scrollHeight > textarea.clientHeight ? 'scroll' : 'hidden'
  mirror.style.visibility = 'hidden'
  mirror.style.pointerEvents = 'none'
  mirror.style.whiteSpace = 'pre-wrap'
  mirror.style.wordWrap = 'break-word'
  mirror.style.font = style.font
  mirror.style.fontSize = style.fontSize
  mirror.style.fontFamily = style.fontFamily
  mirror.style.fontWeight = style.fontWeight
  mirror.style.fontStyle = style.fontStyle
  mirror.style.letterSpacing = style.letterSpacing
  mirror.style.lineHeight = style.lineHeight
  mirror.style.padding = style.padding
  mirror.style.border = style.border
  mirror.style.boxSizing = style.boxSizing

  const textNode = document.createTextNode(value)
  mirror.appendChild(textNode)
  document.body.appendChild(mirror)
  // An empty or detached element has nothing to scroll and ignores these.
  mirror.scrollTop = textarea.scrollTop
  mirror.scrollLeft = textarea.scrollLeft

  try {
    let low = 0
    let high = value.length
    let best = 0
    while (low <= high) {
      const mid = Math.floor((low + high) / 2)
      const range = document.createRange()
      range.setStart(textNode, mid)
      range.setEnd(textNode, Math.min(mid + 1, value.length))
      const box = range.getBoundingClientRect()
      if (box.height === 0 && box.width === 0) {
        high = mid - 1
        continue
      }
      if (clientY < box.top) {
        high = mid - 1
      } else if (clientY > box.bottom) {
        low = mid + 1
        best = mid
      } else if (clientX < box.left) {
        high = mid - 1
      } else {
        low = mid + 1
        best = mid
      }
    }
    return Math.max(0, Math.min(value.length - 1, best))
  } finally {
    mirror.remove()
  }
}
